/* eslint-disable prettier/prettier */
import * as React from "react";
import {
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	TableCell,
	Paper,
	Chip,
} from "@mui/material";
import { Grid, Link } from "@mui/material";
import Pagination from '@mui/material/Pagination';
// app setting imports
import { ImagesContext } from "../Context";
import { formatToUsDateFromUTC } from "../utils/DateHelper";
import { toClass } from "../utils/StringHelper";
import SortComponent from "../common/SortComponent";
interface Props {
  csrfToken: string;
  searchPatients: any;
  setSearchPatients: any;
  checkedList: any;
  setCheckedList: any;
  setOnList: any;
  setPendingAdd: any;
  setPendingRemoval: any;
  onList: any;
  pendingAdd: any;
  pendingRemoval: any;
  paramsPresent: boolean;
  patientListId: string;
  loading: boolean;
  currentPage: number;
  setCurrentPage: Function;
  totalPages: number;
  sortObject: any;
  setSortObject: any;
  allMatchingPatientIds: number[];
  isSelectAllActive: boolean;
  setIsSelectAllActive: Function;
  getAllMatchingPatients: () => any[]; // Function to get all matching patients
}

const preprocessPatients = (patients) => {
  return patients.map((patient) => ({
    ...patient,
    active_program_titles_string: patient.active_program_titles
      ? patient.active_program_titles.join(", ")
      : "",
    diagnosis_code_values_string: patient.diagnosis_code_values
      ? patient.diagnosis_code_values.join(", ")
      : "",
    ltc_facility_names_string: patient.ltc_facility_names
      ? patient.ltc_facility_names.join(", ")
      : "",
    insurance_types_list_string: patient.insurance_types_list
      ? patient.insurance_types_list.join(", ")
      : "",
  }));
};

const SearchResultsWidget: React.FC<Props> = (props: any) => {
  // const [checkedList, setCheckedList] = React.useState<Array<any>>([]);
  const afterFetch = React.useRef(false);
  const images = React.useContext(ImagesContext);

  const setSortOrder = (sortBy, direction) => {
    let sort = {
      field: sortBy,
      direction: direction,
    };
    props.setSortObject(sort);
  };

  const displayArrayField = (arrayField) => {
    return arrayField && arrayField.length > 0 ? arrayField.join(", ") : "";
  };

  const removeFromStatusList = (listType, userId) => {
    switch (listType) {
      case "Pending Add":
        let filteredPendingAdd = props.pendingAdd.filter(
          (id) => id !== userId
        );
        props.setPendingAdd([...filteredPendingAdd]);
        break;
      case "Pending Removal":
        let filteredPendingRemoval = props.pendingRemoval.filter(
          (id) => id !== userId
        );
        props.setPendingRemoval([...filteredPendingRemoval]);
        break;
      case "On List":
        let filteredOnList = props.onList.filter((id) => id !== userId);
        props.setOnList([...filteredOnList]);
        break;
    }
  };

  const addToStatusList = (listType, userId) => {
    switch (listType) {
      case "Pending Add":
        props.setPendingAdd([...props.pendingAdd, userId]);
        break;

      case "Pending Removal":
        props.setPendingRemoval([...props.pendingRemoval, userId]);
        break;

      case "On List":
        props.setOnList([...props.onList, userId]);
        break;
    }
  };

  const handlePatientSelection = (checked: boolean, user) => {
    if (checked) {
      props.setCheckedList([...props.checkedList, user.id]);
      removeFromStatusList(user.list_status, user.id); //remove from old lists
      const status = user.on_list ? "On List" : "Pending Add";
      addToStatusList(status, user.id); //add to new lists
      const updatedPatients = props.searchPatients.map((patient) =>
        patient.id === user.id ? { ...patient, list_status: status } : patient
      );
      props.setSearchPatients(updatedPatients);
      // If select all was active, remove this ID from select all tracking
      if (props.isSelectAllActive) {
        props.setIsSelectAllActive(false);
      }
    } else {
      let filteredList = props.checkedList.filter((id) => id !== user.id);
      props.setCheckedList([...filteredList]);
      removeFromStatusList(user.list_status, user.id); //remove from old lists
      const status = user.on_list ? "Pending Removal" : "Not on List";
      addToStatusList(status, user.id); //add to new lists
      // Revert list_status if unchecked
      const updatedPatients = props.searchPatients.map((patient) =>
        patient.id === user.id ? { ...patient, list_status: status } : patient
      );
      props.setSearchPatients(updatedPatients);
    }
  };

  const selectAll = (checked) => {
    var searchPatients = props.searchPatients;
    var pendingAdd = [...props.pendingAdd];
    var pendingRemoval = [...props.pendingRemoval];
    var onList = [...props.onList];

    if (checked) {
      // Select all: Add all matching patient IDs to checkedList
      props.setIsSelectAllActive(true);
      
      // Get all IDs that match the search criteria
      const allIds = props.allMatchingPatientIds;
      
      // Update checkedList with all matching IDs (avoid duplicates)
      const newCheckedList = [...new Set([...props.checkedList, ...allIds])];
      props.setCheckedList(newCheckedList);

      // Get all matching patients with on_list info
      const allMatchingPatients = props.getAllMatchingPatients();

      // Process ALL matching patients (not just current page) to update status lists
      allMatchingPatients.forEach((patient) => {
        const patientId = patient.id;
        
        // Remove from old status lists if present
        pendingAdd = pendingAdd.filter((id) => id !== patientId);
        pendingRemoval = pendingRemoval.filter((id) => id !== patientId);
        onList = onList.filter((id) => id !== patientId);

        // Determine new status based on on_list property
        const status = patient.on_list ? "On List" : "Pending Add";

        // Add to appropriate status list
        switch (status) {
          case "Pending Add":
            if (!pendingAdd.includes(patientId)) {
              pendingAdd.push(patientId);
            }
            break;

          case "Pending Removal":
            if (!pendingRemoval.includes(patientId)) {
              pendingRemoval.push(patientId);
            }
            break;

          case "On List":
            if (!onList.includes(patientId)) {
              onList.push(patientId);
            }
            break;
        }
      });

      // Update UI status for patients on current page only (status lists already updated above)
      props.searchPatients.forEach((user) => {
        const status = user.on_list ? "On List" : "Pending Add";
        searchPatients = searchPatients.map((patient) =>
          patient.id === user.id ? { ...patient, list_status: status } : patient
        );
      });

      props.setSearchPatients(searchPatients);
      props.setPendingAdd(pendingAdd);
      props.setPendingRemoval(pendingRemoval);
      props.setOnList(onList);
    } else {
      // Deselect all: Remove all matching patient IDs from checkedList
      props.setIsSelectAllActive(false);
      
      // Get all IDs that match the search criteria
      const allIds = props.allMatchingPatientIds;
      
      // Remove all matching IDs from checkedList
      const filteredList = props.checkedList.filter((id) => !allIds.includes(id));
      props.setCheckedList([...filteredList]);

      // Get all matching patients with on_list info
      const allMatchingPatients = props.getAllMatchingPatients();

      // Process ALL matching patients to remove from status lists
      allMatchingPatients.forEach((patient) => {
        const patientId = patient.id;
        
        // Remove from old status lists
        pendingAdd = pendingAdd.filter((id) => id !== patientId);
        pendingRemoval = pendingRemoval.filter((id) => id !== patientId);
        onList = onList.filter((id) => id !== patientId);

        // Determine new status based on on_list property
        const status = patient.on_list ? "Pending Removal" : "Not on List";

        // Add to appropriate status list if needed
        switch (status) {
          case "Pending Add":
            if (!pendingAdd.includes(patientId)) {
              pendingAdd.push(patientId);
            }
            break;

          case "Pending Removal":
            if (!pendingRemoval.includes(patientId)) {
              pendingRemoval.push(patientId);
            }
            break;

          case "On List":
            if (!onList.includes(patientId)) {
              onList.push(patientId);
            }
            break;
        }
      });

      // Update UI status for patients on current page only (status lists already updated above)
      props.searchPatients.forEach((user) => {
        const status = user.on_list ? "Pending Removal" : "Not on List";
        searchPatients = searchPatients.map((patient) =>
          patient.id === user.id ? { ...patient, list_status: status } : patient
        );
      });

      props.setSearchPatients(searchPatients);
      props.setPendingAdd(pendingAdd);
      props.setPendingRemoval(pendingRemoval);
      props.setOnList(onList);
    }
  };

  const isUserChecked = (user) => {
    return props.checkedList.includes(user.id);
  };

  const isSelectAllChecked = () => {
    if (!props.isSelectAllActive || props.allMatchingPatientIds.length === 0) {
      return false;
    }
    // Check if all matching patient IDs are in checkedList
    return props.allMatchingPatientIds.every((id) => props.checkedList.includes(id));
  };

  const formatPatientNameReversed = (firstName, middleName, lastName) => {
    const capitalize = (s) => {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    };

    const formattedFirstName = capitalize(firstName);
    const formattedLastName = capitalize(lastName);

    let formattedMiddleInitial = "";
    if (middleName && middleName.trim() !== "") {
      formattedMiddleInitial = capitalize(middleName.charAt(0));
    }

    return `${formattedLastName}, ${formattedFirstName} ${formattedMiddleInitial}`.trim();
  };

  React.useEffect(() => {
    if (props.searchPatients) {
      const preprocessedPatients = preprocessPatients(props.searchPatients);
      if (
        JSON.stringify(preprocessedPatients) !==
        JSON.stringify(props.searchPatients)
      ) {
        console.log("setSearchPatients SearchResultWidget")
        props.setSearchPatients(preprocessedPatients);
      }
    }
  }, [props.searchPatients]);

  const renderSearchTitle = () => {
    if (!props.paramsPresent && !props.patientListId && props.pendingAdd.length > 0){
      return <h3> Current Pending </h3>
    }
    else if (!props.paramsPresent && props.patientListId) {
      return  <h3> Current List </h3>
    }
    else {
      return  ( 
        <>
          <h3>Search Results</h3>
          <div className="badge">
            {(props.paramsPresent && props.searchPatients) ? props.searchPatients.length : "-" }
          </div>
        </>
      )
    }
  }

  return (
    <>
      <Grid item xs={12}>
        <Grid container>
          <Grid
            container
            justifyContent="space-between"
            className="widget-container"
            sx={{marginRight: '0!important'}}
          >
            <Grid item xs={12} className="widget-header">
              {renderSearchTitle()}
            </Grid>
          </Grid>
          <div className="divider"></div>
          <Grid container>
            <Grid item xs={12} sx={{marginRight: '0!important'}} className="widget-body content-center">
              {props.loading ? (
                <div className="loading-container">
                  <img src={images.new_large_spinner_src}></img>
                  <h3>Loading...</h3>
                  <p>
                    We are loading your patients list.
                    <br />
                    This might take a few miniutes. Please do not leave this
                    page.
                  </p>
                </div>
              ) : props.searchPatients.length > 0 || props.patientListId ? (
                <div className="search-table-container">
                  <TableContainer component={Paper}>
                    <Table
                      stickyHeader
                      className="no-border-table"
                      aria-label="simple table"
                      style={{ minWidth: "1600px" }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            className="table-header"
                            style={{ cursor: "pointer", width: '80px', textAlign: 'center'}}
                          >
                            <div style={{ fontSize: "10px" }}>
                              <b>Select All</b>
                            </div>
                            <input
                              className="user-selection orange-checkbox"
                              type="checkbox"
                              name="selectAll"
                              onChange={(event) => {
                                selectAll(event.target.checked);
                              }}
                              checked={isSelectAllChecked()}
                            />
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              setSortOrder(
                                "last_name",
                                (props.sortObject.field == "last_name" && props.sortObject.direction == "asc")
                                  ? "desc"
                                  : "asc"
                              );
                            }}
                            style={{
                              cursor: "pointer",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header name-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                              Patients Name
                            </strong>
                            {props.sortObject.field == "last_name" ? (
                              <span className="sortIndicator">
                                {props.sortObject.direction == "asc" ? (
                                  <img
                                    src={images.sort_ascending_src}
                                    width="10"
                                    className="sort-icon"
                                    alt="Sort Asc"
                                  />
                                ) : (
                                  <img
                                    src={images.sort_descending_src}
                                    width="10"
                                    className="sort-icon"
                                    alt="Sort Desc"
                                  />
                                )}
                              </span>
                            ) : (
                              <span className="sortIndicator">
                                <img
                                  src={images.sort_plain_src}
                                  width="10"
                                  className="sort-icon"
                                  alt="Sort Asc"
                                />
                              </span>
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            style={{
                              cursor: "default",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                              List Status
                            </strong>
                          </TableCell>
                          <TableCell
                            align="left"
                            style={{
                              cursor: "default",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Program(s)
                            </strong>
                          </TableCell>
                          {/* <SortComponent
                            column_name="active_program_titles_string"
                            column_title="Program(s)"
                            sortObject={props.sortObject}
                            setSortOrder={setSortOrder}
                            className="table-header"
                            sx={{
                              cursor: "pointer",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                          /> */}
                          <TableCell
                            align="left"
                            style={{
                              cursor: "default",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Diagnosis Code(s)
                            </strong>
                          </TableCell>
                          {/* <SortComponent
                            column_name="diagnosis_code_values_string"
                            column_title="Diagnosis Code(s)"
                            sortObject={props.sortObject}
                            setSortOrder={setSortOrder}
                            className="table-header"
                            sx={{
                              cursor: "pointer",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                          /> */}
                          <TableCell
                            align="left"
                            style={{
                              cursor: "default",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Insurance Type(s)
                            </strong>
                          </TableCell>
                          {/* <SortComponent
                            column_name="insurance_types_list_string"
                            column_title="Insurance Type(s)"
                            sortObject={props.sortObject}
                            setSortOrder={setSortOrder}
                            className="table-header"
                            sx={{
                              cursor: "pointer",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                          /> */}
                          <SortComponent
                            column_name="living_facility"
                            column_title="Living Facility"
                            sortObject={props.sortObject}
                            setSortOrder={() => {setSortOrder(
                              "living_facility",
                              (props.sortObject.field == "living_facility" && props.sortObject.direction == "asc")
                                ? "desc"
                                : "asc"
                            );}}
                            className="table-header"
                            sx={{
                              cursor: "pointer",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                          />
                          <TableCell
                            align="left"
                            onClick={() => {
                              setSortOrder(
                                "cgm_present",
                                (props.sortObject.field == "cgm_present" && props.sortObject.direction == "asc")
                                  ? "desc"
                                  : "asc"
                              );
                            }}
                            style={{
                              cursor: "default",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                              CGM Enrolled?
                            </strong>
                            {props.sortObject.field == "cgm_present" ? (
                              <span className="sortIndicator">
                                {props.sortObject.direction == "asc" ? (
                                  <img
                                    src={images.sort_ascending_src}
                                    width="10"
                                    className="sort-icon"
                                    alt="Sort Asc"
                                  />
                                ) : (
                                  <img
                                    src={images.sort_descending_src}
                                    width="10"
                                    className="sort-icon"
                                    alt="Sort Desc"
                                  />
                                )}
                              </span>
                            ) : (
                              <span className="sortIndicator">
                                <img
                                  src={images.sort_plain_src}
                                  width="10"
                                  className="sort-icon"
                                  alt="Sort Asc"
                                />
                              </span>
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              setSortOrder(
                                "date_of_birth",
                                props.sortObject.direction == "asc"
                                  ? "desc"
                                  : "asc"
                              );
                            }}
                            style={{
                              cursor: "pointer",
                              width: "242px",
                              padding: "0px 12px",
                            }}
                            className="table-header"
                          >
                            <strong style={{ fontFamily: "QuicksandMedium" }}>
                              Date of Birth
                            </strong>
                            {props.sortObject.field == "date_of_birth" ? (
                              <span className="sortIndicator">
                                {props.sortObject.direction == "asc" ? (
                                  <img
                                    src={images.sort_ascending_src}
                                    width="10"
                                    className="sort-icon"
                                    alt="Sort Asc"
                                  />
                                ) : (
                                  <img
                                    src={images.sort_descending_src}
                                    width="10"
                                    className="sort-icon"
                                    alt="Sort Desc"
                                  />
                                )}
                              </span>
                            ) : (
                              <span className="sortIndicator">
                                <img
                                  src={images.sort_plain_src}
                                  width="10"
                                  className="sort-icon"
                                  alt="Sort Asc"
                                />
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <>
                          {props.searchPatients?.map((user, index) => (
                            <TableRow
                              key={index}
                              className={index % 2 == 0 ? "rowEven" : "row"}
                            >
                              <TableCell
                                size="small"
                                scope="row"
                                align="center"
                                className="tableCell"
                              >
                                <input
                                  className="user-selection orange-checkbox"
                                  type="checkbox"
                                  name="selectCustomer"
                                  onChange={(event) => {
                                    handlePatientSelection(
                                      event.target.checked,
                                      user
                                    );
                                  }}
                                  checked={isUserChecked(user)}
                                />
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                {formatPatientNameReversed(
                                  user.first_name,
                                  user.middle_name,
                                  user.last_name
                                )}
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                <Chip
                                  size="small"
                                  className={`custom-badge ${toClass(
                                    user.list_status
                                  )}`}
                                  label={user.list_status}
                                />
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                {displayArrayField(user.active_program_titles)}
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                {displayArrayField(user.diagnosis_code_values)}
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                {displayArrayField(user.insurance_types_list)}
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                {displayArrayField(user.ltc_facility_names)}
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                {user.cgm_enabled === true ? "Yes" : "No"}
                              </TableCell>
                              <TableCell
                                size="small"
                                scope="row"
                                align="left"
                                className="tableCell"
                              >
                                <p className="table-body-text">
                                  {user.date_of_birth
                                    ? formatToUsDateFromUTC(user.date_of_birth)
                                    : ""}
                                </p>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {
                    props.totalPages > 0 &&
                      <Pagination
                        count={props.totalPages}
                        // count={100}
                        page={props.currentPage}
                        onChange={(_event: React.ChangeEvent, page: number) => props.setCurrentPage(page)}
                        sx={{ margin: '0 auto', paddingTop: '16px' }}
                        boundaryCount={3}
                      />
                  }
                </div>
              ) : (
                <div>To search for a patient, enter criteria in the fields</div>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SearchResultsWidget;
